'use client';

import type { StateStorage } from 'zustand/middleware';

import { handleError, localStorageAvailable } from '@infor/utils/src';

import { getMonitoringService } from 'lib/observability/monitoring/monitoring.impl';

const inMemoryStorage = new Map<string, string>();

let cachedStorageAvailability: boolean | null = null;

const isStorageAccessible = () => {
  if (typeof window === 'undefined') return false;

  if (cachedStorageAvailability !== null) {
    return cachedStorageAvailability;
  }

  cachedStorageAvailability = localStorageAvailable();

  return cachedStorageAvailability;
};

const rememberValue = (name: string, value: string | null) => {
  if (value === null || value == undefined) {
    inMemoryStorage.delete(name);

    return;
  }

  inMemoryStorage.set(name, value);
};

export const safeStateStorage: StateStorage = {
  getItem: (name) => {
    if (!isStorageAccessible()) {
      return inMemoryStorage.get(name) ?? null;
    }

    try {
      const value = window.localStorage.getItem(name);

      rememberValue(name, value);

      return value;
    } catch (error) {
      cachedStorageAvailability = false;
      handleError(error);

      getMonitoringService()?.captureError({
        error: error as Error,
        componentName: 'safeStateStorage.getItem',
        context: {
          name,
          local: 'localStorage',
        },
      });

      return inMemoryStorage.get(name) ?? null;
    }
  },
  setItem: (name, value) => {
    rememberValue(name, value);

    if (!isStorageAccessible()) {
      return;
    }

    try {
      window.localStorage.setItem(name, value);
    } catch (error) {
      cachedStorageAvailability = false;
      getMonitoringService()?.captureError({
        error: error as Error,
        componentName: 'safeStateStorage.setItem',
        context: {
          name,
          local: 'localStorage',
        },
      });
      handleError(error);
    }
  },
  removeItem: (name) => {
    inMemoryStorage.delete(name);

    if (!isStorageAccessible()) {
      return;
    }

    try {
      window.localStorage.removeItem(name);
    } catch (error) {
      cachedStorageAvailability = false;
      getMonitoringService()?.captureError({
        error: error as Error,
        componentName: 'safeStateStorage.removeItem',
        context: {
          name,
          local: 'localStorage',
        },
      });
      handleError(error);
    }
  },
};
